import React, { useEffect, useState } from 'react';
import { database } from '../../../utils/firebase';
import { getAuth, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { onValue, ref, update } from 'firebase/database';
import account from './AccountSettings.module.css';
import { useNavigate } from "react-router-dom";
import {Switch, Typography} from "@mui/joy";

const Account = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    const [firstName, setFirstName] = useState(user ? user.displayName : '');
    const [lastName, setLastName] = useState(user ? user.displayName : '');
    const [email, setEmail] = useState(user ? user.email : '');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [message, setMessage] = useState('');
    const navigate = useNavigate();
    const [previewEnabled, setPreviewEnabled] = useState(false);

    useEffect(() => {
        if (user) {
            const userRef = ref(database, `people/${user.uid}`);
            onValue(userRef, (snapshot) => {
                const data = snapshot.val();
                if (data) {
                    setFirstName(data.nameFirst);
                    setLastName(data.nameLast);
                    setPreviewEnabled(data.preview)
                }
            });
        }
    }, [user]);

    const handleNameSubmit = async (e) => {
        e.preventDefault();
        if (user) {
            try {
                await update(ref(database, `people/${user.uid}`), { nameFirst: firstName, nameLast: lastName });
                setMessage('Name updated successfully!');
            } catch (error) {
                setMessage(`Error: ${error.message}`);
            }
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (user) {
            try {
                await updateEmail(user, email);
                setMessage('Email updated successfully!');
            } catch (error) {
                setMessage(`Error: ${error.message}`);
            }
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (user) {
            try {
                const credential = EmailAuthProvider.credential(user.email, password);
                await reauthenticateWithCredential(user, credential);
                await updatePassword(user, newPassword);
                setMessage('Password updated successfully!');
            } catch (error) {
                setMessage(`Error: ${error.message}`);
            }
        }
    };

    const handlePreviewSubmit = async (e) => {
        e.preventDefault();
        if (user) {
            try {
                await update(ref(database, `people/${user.uid}`), { preview: previewEnabled });
                setMessage('App version updated!');
            } catch (error) {
                setMessage(`Error: ${error.message}`);
            }
        }
    }

    return (
        <div className={account['account-container']}>
            <div className={account['account-card']}>
                <h2>Update Name</h2>
                <form onSubmit={handleNameSubmit}>
                    <div className={account['form-group']}>
                        <label>First Name</label>
                        <input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="Enter your new first name"
                            required
                        />
                        <br/>
                        <br/>
                        <label>Last Name</label>
                        <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Enter your new last name"
                            required
                        />
                    </div>
                    <button type="submit" className={account['account-button']}>Update Name</button>
                </form>
            </div>

            <div className={account['account-card']}>
                <h2>Update Email</h2>
                <form onSubmit={handleEmailSubmit}>
                    <div className={account['form-group']}>
                        <label>New Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your new email"
                            required
                        />
                    </div>
                    <button type="submit" className={account['account-button']}>Update Email</button>
                </form>
            </div>

            <div className={account['account-card']}>
                <h2>Update Password</h2>
                <form onSubmit={handlePasswordSubmit}>
                    <div className={account['form-group']}>
                        <label>Current Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your current password"
                            required
                        />
                    </div>
                    <div className={account['form-group']}>
                        <label>New Password</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter your new password"
                            required
                        />
                    </div>
                    <button type="submit" className={account['account-button']}>Update Password</button>
                </form>
            </div>

            <div className={account['account-card']}>
                <h2>App Version</h2>
                <form onSubmit={handlePreviewSubmit}>
                    <div className={account['form-group']}>
                        <Switch
                            color={previewEnabled ? "warning" : "success"}
                            variant="solid"
                            value={previewEnabled}
                            onChange={(e) => setPreviewEnabled(e.target.checked)}
                            slotProps={{
                                track: {
                                    children: (
                                        <React.Fragment>
                                            <div style={{marginLeft: '35px'}}>
                                                {previewEnabled && (
                                                    <div>
                                                        <Typography component="span" level="h1" sx={{color: 'white'}}>
                                                            Preview
                                                        </Typography>
                                                        <Typography component="span" level="h1" sx={{color: 'white'}}>
                                                            Version
                                                        </Typography>
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{marginRight: '40px'}}>
                                                {!previewEnabled && (
                                                        <div>
                                                            <Typography component="span" level="h1" sx={{color: 'white'}}>
                                                                Stable
                                                            </Typography>
                                                            <Typography component="span" level="h1" sx={{color: 'white'}}>
                                                                Version
                                                            </Typography>
                                                        </div>
                                                )}
                                            </div>
                                        </React.Fragment>
                                    ),
                                },
                            }}
                            sx={{
                                '--Switch-thumbSize': '130px',
                                '--Switch-trackWidth': '316px',
                                '--Switch-trackHeight': '140px',
                                "--Switch-trackRadius": "70px"
                            }}
                        />
                    </div>
                    <button type="submit" className={account['account-button']}>Update Version</button>
                </form>
            </div>

            {message && <p>{message}</p>}

            <div className={account['account-footer']}>
                <button
                    onClick={() => navigate("/app")}
                    className={account['text-link']}
                    style={{ background: "none", border: "none", color: "inherit", cursor: "pointer" }}
                >
                    Return to Dashboard
                </button>
            </div>
        </div>
    );
};

export default Account;